/* ═══════════════════════════════════════════════════════════════════════════
   UI — shared presentational primitives (section header + modal dialogs) used
   across multiple feature areas. Extracted from App.jsx (2026-05-29); see
   docs/superpowers/specs/2026-05-29-app-jsx-decomposition-design.md
   ═══════════════════════════════════════════════════════════════════════════ */
import { useState } from "react";
import { CL, FONT } from './theme.js';
import { curSym } from './utils';

// Cormorant section header with hairline rules — left short (8px), right flex-fill.
// Used on Today screen across check-ins / check-outs / in-house / actions.
function SectionHeader({ children, mt = 18, mb = 10, mx = 14 }) {
  return (
    <div data-sec-h style={{
      display: "flex", alignItems: "center", gap: 10,
      margin: `${mt}px ${mx}px ${mb}px`,
      fontFamily: FONT.display, fontWeight: 500, fontSize: 16, color: CL.bark, lineHeight: 1.1,
    }}>
      <span style={{ width: 8, height: 1, background: CL.goldL, flexShrink: 0 }} />
      <span>{children}</span>
      <span style={{ flex: 1, height: 1, background: CL.goldL }} />
    </div>
  );
}

function ConfirmModal({ title, body, cancelLabel, confirmLabel, onCancel, onConfirm }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: CL.cream, borderRadius: 12, padding: 20, maxWidth: 400, width: '100%' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: CL.bark, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 14, color: CL.stone, marginBottom: 18, lineHeight: 1.5 }}>{body}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '10px 16px', background: 'transparent', color: CL.stone, border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>{cancelLabel}</button>
          <button onClick={onConfirm} style={{ padding: '10px 16px', background: CL.terra, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function DeleteRoomModal({ room, lang, onCancel, onConfirm }) {
  const [typed, setTyped] = useState('');
  const matches = typed === room.name;

  const L = lang === 'bg' ? {
    title: `Изтрий стая ${room.name}?`,
    body: 'Това действие не може да бъде отменено. За потвърждение, напиши името на стаята.',
    cancel: 'Отказ',
    del: 'Изтрий',
  } : {
    title: `Delete room ${room.name}?`,
    body: "This cannot be undone. To confirm, type the room's name below.",
    cancel: 'Cancel',
    del: 'Delete',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: CL.cream, borderRadius: 12, padding: 20, maxWidth: 400, width: '100%' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: CL.bark, marginBottom: 10 }}>{L.title}</div>
        <div style={{ fontSize: 14, color: CL.stone, marginBottom: 12, lineHeight: 1.5 }}>{L.body}</div>
        <input value={typed} onChange={e => setTyped(e.target.value)} autoFocus
          style={{ width: '100%', padding: '10px 12px', border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff', color: CL.bark, marginBottom: 18 }} />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '10px 16px', background: 'transparent', color: CL.stone, border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>{L.cancel}</button>
          <button onClick={onConfirm} disabled={!matches}
            style={{ padding: '10px 16px', background: matches ? CL.terra : CL.goldL, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: matches ? 'pointer' : 'not-allowed', opacity: matches ? 1 : 0.6 }}>{L.del}</button>
        </div>
      </div>
    </div>
  );
}

function DepositAmountModal({ guestName, currency, channel, lang, onCancel, onSubmit }) {
  const [amount, setAmount] = useState('');
  const cur = curSym(currency || 'EUR');

  const channelLabels = lang === 'bg'
    ? { whatsapp: 'WhatsApp', viber: 'Viber', email: 'имейл' }
    : { whatsapp: 'WhatsApp', viber: 'Viber', email: 'email' };

  const L = lang === 'bg'
    ? {
        title: 'Изпращане на искане за депозит',
        body:  `Каква сума да поискаме от ${guestName || 'гост'}?`,
        cancel: 'Отказ',
        send:  `Изпрати чрез ${channelLabels[channel] || ''}`,
      }
    : {
        title: 'Send deposit request',
        body:  `How much should we ask ${guestName || 'guest'} for?`,
        cancel: 'Cancel',
        send:  `Send via ${channelLabels[channel] || ''}`,
      };

  const num = Number(amount);
  const canSend = Number.isFinite(num) && num > 0;

  function handleSubmit() {
    if (!canSend) return;
    onSubmit(num);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: CL.cream, borderRadius: 12, padding: 20, maxWidth: 400, width: '100%' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: CL.bark, marginBottom: 8 }}>{L.title}</div>
        <div style={{ fontSize: 14, color: CL.stone, marginBottom: 14, lineHeight: 1.5 }}>{L.body}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
          <span style={{ fontSize: 16, color: CL.stone }}>{cur}</span>
          <input
            autoFocus
            type="number" inputMode="decimal" placeholder="0" min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSend) handleSubmit(); }}
            style={{ flex: 1, padding: '10px 12px', border: `1px solid ${CL.goldL}`, borderRadius: 8, fontSize: 16, background: '#fff', color: CL.bark, fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '10px 16px', background: 'transparent', color: CL.stone, border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>{L.cancel}</button>
          <button
            onClick={handleSubmit}
            disabled={!canSend}
            style={{ padding: '10px 16px', background: canSend ? CL.olive : CL.goldL, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: canSend ? 'pointer' : 'not-allowed' }}
          >
            {L.send}
          </button>
        </div>
      </div>
    </div>
  );
}

export { SectionHeader, ConfirmModal, DeleteRoomModal, DepositAmountModal };
